import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box} from 'views/components';
import {translate} from '../Translate';

import backend from 'utils/backend';

import CommandResponseView from './CommandResponseView';
import SlashCommandTextbox from './SlashCommandTextbox';

const Commands = (props) => {
  const {
    communicationTarget,
    requester,
    resources,
    statusProgressWrap: progressWrap,
    playbook,
  } = props;

  const requesterRn = requester.userRn;
  const [commandExecutionResponse, setCommandExecutionResponse] = React.useState(null);
  const [lastExecutedSkill, setLastExecutedSkill] = React.useState(null);
  const [isExecutingCommand, setIsExecutingCommand] = React.useState(false);
  const [playbooks, setPlaybooks] = React.useState([]);

  const executeSkill = React.useCallback(async (skill, promptText) => {
    let response = null;

    await progressWrap(async () => {
      try {
        setIsExecutingCommand(true);

        const requestBody = {
          ressources: [
            ...resources,
            {type: 'USER', rn: requesterRn},
          ],
          text: promptText || ' ',
        };

        setLastExecutedSkill({
          promptText,
          skill,
        });

        response = await backend.skills.executeSkill(skill.skillRn, requestBody);
        setCommandExecutionResponse(response.result);
      } finally {
        setIsExecutingCommand(false);
      }
    }, {
      showProgress: false,
    });

    return response;
  }, [progressWrap, requesterRn, resources]);

  const handleRegenerate = React.useCallback(() => {
    if (!lastExecutedSkill) {
      return null;
    }

    return executeSkill(lastExecutedSkill.skill, lastExecutedSkill.promptText);
  }, [executeSkill, lastExecutedSkill]);

  const handleDiscard = React.useCallback(() => {
    setCommandExecutionResponse(null);
  }, []);

  const executePlaybook = React.useCallback(async (selectedPlaybook) => {
    const primaryResource = resources[0];

    await progressWrap(async () => {
      try {
        setIsExecutingCommand(true);
        await playbook.execute(selectedPlaybook.playbookRn, primaryResource.rn);
      } finally {
        setIsExecutingCommand(false);
      }
    }, {
      showProgress: false,
    });
  }, [playbook, progressWrap, resources]);

  React.useEffect(() => {
    let isMounted = true;

    const loadPlaybooks = async () => {
      try {
        const response = await backend.playbook.list();

        if (!isMounted) {
          return;
        }

        setPlaybooks(response?.list || []);
      } catch {
        if (isMounted) {
          setPlaybooks([]);
        }
      }
    };

    loadPlaybooks();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box>
      {
        commandExecutionResponse &&
        <CommandResponseView
          communicationTarget={communicationTarget}
          requester={requester}
          response={commandExecutionResponse}
          onDiscard={handleDiscard}
          isRegenerating={isExecutingCommand}
          onRegenerate={handleRegenerate}
        />
      }
      <SlashCommandTextbox
        isExecutingCommand={isExecutingCommand}
        onExecutePlaybook={executePlaybook}
        onExecuteSkill={executeSkill}
        placeholder={translate('playbooks.List.ExecutePlaceholder')}
        playbooks={playbooks}
      />
    </Box>
  );
};

Commands.propTypes = {
  communicationTarget: PropTypes.shape({
    email: PropTypes.string,
    sendEmail: PropTypes.func,
    sendSms: PropTypes.func,
    sms: PropTypes.string,
  }),
  requester: PropTypes.object.isRequired,
  resources: PropTypes.arrayOf(PropTypes.shape({
    rn: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  })).isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  playbook: PropTypes.shape({
    execute: PropTypes.func,
  }),
};

const mapStateToProps = (state) => ({
  requester: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(Commands));
